<template>
  <div class="d-flex flex-column" style="gap: 6px">
    <v-row align="center" justify="space-between">
      <v-col cols="auto">
        <h4>Bilder als ZIP-Datei hochladen (optional)</h4>
      </v-col>
      <v-col class="text-right" cols="auto">
        <v-btn
          color="primary"
          size="small"
          variant="outlined"
          @click="downloadTemplate"
        >
          <v-icon color="primary" start size="small">mdi-download</v-icon>
          Bildvorlage
        </v-btn>
      </v-col>
    </v-row>
    <span class="mt-1">
      Erstellen Sie eine ZIP-Datei mit allen Bildern und verwenden Sie die
      gleichen Bildnamen in der Excel-/CSV-Datei.
    </span>

    <!-- TODO: replace this with file-upload-component after vue3 migration -->
    <div class="d-flex flex-column" style="gap: 6px">
      <div
        v-if="!zipFile"
        class="file-input-group"
        @dragover.prevent="onDragOver"
        @dragleave.prevent="onDragLeave"
        @drop.prevent="onDrop"
      >
        <div
          class="upload-section text-center pa-8 rounded-lg"
          :class="{ 'drag-active': isDragging }"
        >
          <input
            ref="zipInput"
            type="file"
            accept=".zip"
            class="d-none"
            @change="handleZipUpload"
          />
          <v-icon size="32" class="mb-2">mdi-folder-upload-outline</v-icon>
          <p class="mb-1 font-weight-medium">
            ZIP-Datei mit Bildern per Drag & Drop verschieben
          </p>
          <p class="mb-2">or</p>
          <v-btn
            class="bg-secondary"
            color="black"
            variant="text"
            @click="$refs.zipInput.click()"
          >
            ZIP-Datei auswählen
          </v-btn>
          <p class="mt-3 text-caption text-grey">
            Gültige Bildformate: jpg, jpeg, png, svg | Max. ZIP-Dateigröße: 25
            MB
          </p>
        </div>
      </div>
      <v-card
        v-if="zipFile"
        border
        elevation="1"
        class="uploaded-card pa-5 mt-3"
      >
        <div class="d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon color="deep-purple" class="mr-3">mdi-zip-box</v-icon>
            <div>
              <span>{{ zipFile.name }}</span>
              <div class="text-caption text-grey">{{ zipFile.size }}</div>
            </div>
          </div>
          <v-icon @click="removeZipFile">mdi-delete</v-icon>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
import JSZip from "jszip";
import ToastService from "@/services/ToastService";
import { mapActions } from "vuex";
import { fileTypeFromBuffer } from "file-type";

const imageExtensions = process.env.VUE_APP_ALLOWED_EXT_IMAGES;

export default {
  name: "ZipUpload",
  props: {
    type: String,
  },
  data() {
    return {
      zipFile: null,
      isDragging: false,
    };
  },
  methods: {
    ...mapActions({
      addToast: "toasts/add",
    }),
    async handleZipUpload(e) {
      const file = e.target.files[0];
      if (!file) return;
      const MAX_SIZE = 25 * 1024 * 1024;
      if (file.size > MAX_SIZE) {
        this.addToast(
          ToastService.createToast("zipUpload.fileTooLarge", "error")
        );
        this.$refs.zipInput.value = null;
        return;
      }
      const ext = file.name.split(".").pop().toLowerCase();
      if (ext !== "zip") {
        this.addToast(
          ToastService.createToast("zipUpload.invalidFormat", "error")
        );
        this.$refs.zipInput.value = null;
        return;
      }
      this.zipFile = {
        name: file.name,
        size: this.formatBytes(file.size),
      };
      try {
        const images = await this.extractImagesFromZip(file);
        this.$emit("file-uploaded", images);
      } catch (error) {
        console.error("Error extracting ZIP:", error);
      }
    },
    async extractImagesFromZip(file) {
      const jszip = new JSZip();
      const zip = await jszip.loadAsync(file);
      const imageFiles = [];
      const allowedExt = imageExtensions?.toLowerCase()?.split(",");

      for (const [fileName, zipEntry] of Object.entries(zip.files)) {
        if (zipEntry.dir) continue;
        const arrayBuffer = await zipEntry.async("arraybuffer");
        const fileType = await fileTypeFromBuffer(new Uint8Array(arrayBuffer));
        const lowerName = fileName.toLowerCase();
        const ext = lowerName.split(".").pop();

        if (!allowedExt.includes(ext)) continue;

        const blob = await zipEntry.async("blob");
        const blobUrl = URL.createObjectURL(blob);
        imageFiles.push({
          name: fileName.split("/").pop(),
          mime: fileType.mime,
          blobUrl,
          file: blob,
        });
      }
      return imageFiles;
    },
    removeZipFile() {
      this.zipFile = null;
      this.$nextTick(() => {
        if (this.$refs.zipInput) {
          this.$refs.zipInput.value = null;
        }
      });
      this.$emit("file-removed");
    },
    formatBytes(bytes) {
      const sizes = ["Bytes", "KB", "MB", "GB"];
      if (bytes === 0) return "0 Bytes";
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      return (
        parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + " " + sizes[i]
      );
    },
    onDragOver() {
      this.isDragging = true;
    },
    onDragLeave() {
      this.isDragging = false;
    },
    onDrop(e) {
      this.isDragging = false;
      const file = e.dataTransfer.files[0];
      if (file) this.handleZipUpload({ target: { files: [file] } });
    },
    downloadTemplate() {
      const link = document.createElement("a");
      link.href =
        this.type === "bookables"
          ? process.env.VUE_APP_BOOKABLE_ZIP
          : process.env.VUE_APP_EVENT_ZIP;
      link.setAttribute("download", "bookable_import_template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
  },
};
</script>

<style scoped>
.v-theme--light.v-icon {
  color: rgba(0, 0, 0, .54);
}
.upload-section {
  border: 2px dashed #ccc;
  transition: border-color 0.2s, background-color 0.2s, box-shadow 0.2s;
}
.upload-section.drag-active {
  border-color: #673ab7;
  background-color: #ede7f6;
  box-shadow: 0 0 10px rgba(103, 58, 183, 0.2);
}
.uploaded-card {
  border-left: 4px solid #673ab7;
  transition: box-shadow 0.2s;
  border-radius: 12px !important;
}
.font-weight-medium {
  font-weight: 500;
}
</style>
